/**
 * PaperSkeleton — shimmer placeholder shown in the right pane
 * while a meeting is loading from the API or while a transform
 * mutation is in flight.
 *
 * Shape mimics the real paper geometry (8.5×11" letter sheet with
 * the wide left margin) so the layout doesn't jump when content
 * arrives. Sections approximate: header band, metadata strip,
 * attendees row, three topic blocks. Pulses via Tailwind's
 * `animate-pulse` so the skeleton reads as "loading" without
 * being noisy.
 */
export function PaperSkeleton() {
  return (
    <article
      className="relative mx-auto bg-white shadow-[0_2px_16px_rgba(0,0,0,0.10)] ring-1 ring-black/5"
      style={{
        width: '8.5in',
        height: '11in',
        paddingTop: '1in',
        paddingRight: '0.7in',
        paddingBottom: '1.51in',
        paddingLeft: '2.85in',
      }}
      aria-busy="true"
      aria-label="Document preview loading"
    >
      {/* Logo placeholder in left margin */}
      <div
        className="absolute animate-pulse rounded bg-charcoal/10"
        style={{
          top: '0.5in',
          left: '0.7in',
          width: '1.3in',
          height: '0.5in',
        }}
      />

      {/* Office ribbon placeholder */}
      <div
        className="absolute space-y-2"
        style={{ left: '0.7in', bottom: '0.8in', width: '1.5in' }}
      >
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="h-2 animate-pulse rounded bg-charcoal/8"
            style={{ animationDelay: `${i * 60}ms` }}
          />
        ))}
      </div>

      {/* Main content column placeholders */}
      <div className="space-y-5">
        {/* Title row */}
        <div
          className="h-5 w-3/4 animate-pulse rounded bg-charcoal/15"
          style={{ animationDelay: '0ms' }}
        />

        {/* Metadata strip — 3 columns */}
        <div className="grid grid-cols-3 gap-3 pt-1">
          {[0, 1, 2].map((i) => (
            <div key={i} className="space-y-1">
              <div
                className="h-2 w-1/2 animate-pulse rounded bg-charcoal/12"
                style={{ animationDelay: `${i * 80}ms` }}
              />
              <div
                className="h-3 animate-pulse rounded bg-charcoal/8"
                style={{ animationDelay: `${i * 80 + 40}ms` }}
              />
            </div>
          ))}
        </div>

        {/* Attendees row */}
        <div className="space-y-2 pt-3">
          <div className="h-2 w-20 animate-pulse rounded bg-charcoal/12" />
          <div className="flex flex-wrap gap-1.5">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-4 animate-pulse rounded-full bg-charcoal/8"
                style={{
                  width: `${60 + i * 12}px`,
                  animationDelay: `${i * 50}ms`,
                }}
              />
            ))}
          </div>
        </div>

        {/* Topic blocks */}
        {[0, 1, 2].map((t) => (
          <div key={t} className="space-y-2 pt-2">
            <div
              className="h-3 w-2/5 animate-pulse rounded bg-charcoal/14"
              style={{ animationDelay: `${t * 100}ms` }}
            />
            <div className="space-y-1.5 pl-3">
              {[0, 1, 2].map((b) => (
                <div
                  key={b}
                  className="h-2 animate-pulse rounded bg-charcoal/8"
                  style={{
                    width: `${85 - b * 10}%`,
                    animationDelay: `${t * 100 + b * 50}ms`,
                  }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Soft "Generating…" badge bottom-center */}
      <div
        className="absolute left-1/2 -translate-x-1/2 rounded-full border border-pine/20 bg-cloud/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-pine/80 shadow-sm"
        style={{ bottom: '0.6in' }}
      >
        Loading…
      </div>
    </article>
  );
}
